import React, { useEffect } from 'react';
import styles from './StoreBanner.module.css';
import { MuiButton, MuiChip, MuiSelector, MuiDialogue } from '@ace/ace-ui-web';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StoreBannerMobile from './StoreBannerMobile';
import lang from '../../languages/en.json';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  selectUserDetails,
  selectIsMultiStoreEnabled,
  setCurrentStoreNumber,
  setIsMultiStoreEnabled,
} from '../../redux/UserReducer/userSlice';
import {
  fetchHGReportData,
  resetHGReportData,
  selectHGReportData,
  selectHGReportLoading,
} from '../../redux/HeaderReducer/hgReportSlice';
import { sortStoreList, getCurrentStoreDetails } from '../../utils/appUtils';
import { useClickHandler } from '../../utils/usageMetricsLogger';
import { useBuildDynamicUrl } from '../../hooks/useDynamicUrl';
import { generateHGProgressReport } from '../../utils/hgReportExcel';
import { HGReportResponse } from '../../models/HGReportModel';
import {
  fetchHigherGroundStatus,
  selectHigherGroundStatus,
} from '../../redux/HeaderReducer/higherGroundStatusSlice';
import {
  selectPeerGroupDescription,
  selectPeerGroupLoadedMode,
} from '../../redux/PerformanceReducer/Ecommerce/ecommSalesPerfSlice';

interface StoreBannerProps {
  /**
   * Higher Ground status
   */
  higherGroundStatus?: string;
  /**
   * Logo image URL for Higher Ground
   */
  higherGroundLogoImg?: string;
  /**
   * Angled divider SVG URL
   */
  dividerSvgUrl?: string;
  /**
   * Callback when Performance menu item is clicked
   */
  onPerformanceClick?: () => void;
  /**
   * Callback when Operations menu item is clicked
   */
  onOperationsClick?: () => void;
  /**
   * Callback when ADW Report is clicked
   */
  onADWReportClick?: () => void;
  onADWDashboardClick?: () => void;
  /**
   * Callback when Multi-Store toggle is changed
   */
  onMultiStoreToggle?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  /**
   * Callback when Print is clicked
   */
  onPrintClick?: (items: string[]) => void;
  /** When true, disables the print menu while data is still loading */
  isPrintDisabled?: boolean;
  /**
   * Callback when Summary Download is clicked from the mobile drawer
   */
  onSummaryClick?: () => void;
}

const isHGReportEmpty = (report: HGReportResponse | null): boolean =>
  !report ||
  (!report.rankDate &&
    !report.currentRankCode &&
    !report.priorRankCode &&
    report.metrics.length === 0);

const StoreBanner = ({
  higherGroundStatus = 'N/A',
  higherGroundLogoImg = '/images/hg_logo.svg',
  onPerformanceClick,
  onOperationsClick,
  onADWReportClick,
  onADWDashboardClick,
  onMultiStoreToggle,
  onPrintClick,
  isPrintDisabled = false,
  onSummaryClick,
}: StoreBannerProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState<boolean>(false);
  const [isProgressReportDialogOpen, setIsProgressReportDialogOpen] =
    React.useState<boolean>(false);

  // Get user details and higher ground status from Redux store
  const userDetails = useAppSelector(selectUserDetails);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);
  const higherGroundStatusData = useAppSelector(selectHigherGroundStatus);
  const peerGroupDescription = useAppSelector(selectPeerGroupDescription);
  const peerGroupLoadedMode = useAppSelector(selectPeerGroupLoadedMode);
  const hgReportData = useAppSelector(selectHGReportData);
  const isHGReportLoading = useAppSelector(selectHGReportLoading);
  const dispatch = useAppDispatch();

  // Reset HG report data when store changes so the next button click re-fetches
  useEffect(() => {
    dispatch(resetHGReportData());
  }, [userDetails?.currentStoreNumber, dispatch]);

  // Fetch Higher Ground status when store changes
  // Always use currentStoreNumber (not parent store) even in multi-store mode
  useEffect(() => {
    const currentStoreNumber = userDetails?.currentStoreNumber;
    if (!currentStoreNumber) return;
    dispatch(
      fetchHigherGroundStatus({
        storeNumber: currentStoreNumber,
        isMultiStoreMode: false, // Always treat as single store for Higher Ground status
      }),
    );
  }, [userDetails?.currentStoreNumber, dispatch]);

  // Map userChainList to MuiSelector options
  // With useMemo the options array is only recalculated when userDetails?.userChainList actually changes
  const userChainList = React.useMemo(() => {
    if (!userDetails?.userChainList || userDetails.userChainList.length === 0) {
      return [];
    }
    return sortStoreList(userDetails.userChainList).map(store => ({
      value: store.storeNumber,
      label: store.storeNumber,
    }));
  }, [userDetails?.userChainList]);

  // Get current store details based on currentStoreNumber
  // With useMemo the find utility call only runs when the dependency array values change
  const currentStore = React.useMemo(
    () => getCurrentStoreDetails(userDetails?.currentStoreNumber, userDetails?.userChainList),
    [userDetails?.currentStoreNumber, userDetails?.userChainList],
  );

  // Compute normalized Higher Ground status once to avoid duplication
  const normalizedHigherGroundStatus = (
    higherGroundStatusData?.rankCode?.trim() || higherGroundStatus
  ).toUpperCase();

  // Peer group / multi-store group name for the tooltip. Only show the loaded name when it
  // matches the active mode — otherwise show a dash. This prevents a stale name from the
  // previous mode (or a partial value mid-fetch) from flashing during a single↔multi switch.
  const peerGroupName = peerGroupLoadedMode === isMultiStoreEnabled && peerGroupDescription
      ? peerGroupDescription
      : '-';

  // MobileQuery Responsive
  const isTablet = useMediaQuery('(max-width:1165px)');
  const isMobile = useMediaQuery('(max-width:995px)');

  const handleStoreChange = (storeId: string) => {
    dispatch(setCurrentStoreNumber(storeId));
    dispatch(setIsMultiStoreEnabled(false));
  };

  const handleMenuClick = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleAndLogClick = useClickHandler('Header');

  const baseUrlType = 'RETAILER';
  const buildDynamicUrl = useBuildDynamicUrl();

  // Handler for Progress Report button — fetches data if not already cached, then generates
  // and downloads the Excel report. Opens an error dialog if data is null or the fetch fails.
  const handleProgressReportClick = React.useCallback(async (): Promise<void> => {
    const currentStoreNumber = userDetails?.currentStoreNumber;
    const storeName = currentStore?.storeName ?? '';

    try {
      let reportData = hgReportData;

      if (!reportData) {
        reportData = await dispatch(
          fetchHGReportData({
            storeNumber: currentStoreNumber ?? '',
            isMultiStoreMode: false,
          }),
        ).unwrap();
      }

      if (isHGReportEmpty(reportData)) {
        setIsProgressReportDialogOpen(true);
        return;
      }

      await generateHGProgressReport(reportData, currentStoreNumber ?? '', storeName);
    } catch (error) {
      console.error('[HG Report] Failed to generate HG Progress Report:', error);
      setIsProgressReportDialogOpen(true);
    }
  }, [userDetails?.currentStoreNumber, currentStore?.storeName, hgReportData, dispatch]);

  // Handler to close Progress Report dialog
  const handleCloseProgressReportDialog = React.useCallback((): void => {
    setIsProgressReportDialogOpen(false);
  }, []);

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Grid
          container
          spacing={0}
          className={styles.topHeader}
          wrap="nowrap"
          sx={{ width: '100%', maxWidth: '100%' }}>
          {/* Store Info Section */}
          <Grid
            size={4}
            className={styles.gridStoreInfo}>
            <div className={styles.storeInfoSection}>
              <div className={styles.storeInfoBackground} />
              <div className={styles.storeInfoDivider} />
              {isMobile && (
                <div className={styles.menuIconContainer}>
                  <MenuIcon
                    className={styles.menuIcon}
                    onClick={handleMenuClick}
                  />
                </div>
              )}
              <div className={styles.storeInfoContent}>
                <div className={styles.storeNameContainer}>
                  <p className={styles.storeName}>{currentStore?.storeName ?? ''}</p>
                  <p className={styles.storeNumber}>(#{userDetails?.currentStoreNumber ?? ''})</p>
                </div>
                <p className={styles.storeLocation}>
                  {currentStore?.storeCity && currentStore?.storeState
                    ? `${currentStore.storeCity}, ${currentStore.storeState}`
                    : ''}
                </p>
                <Tooltip
                  title={peerGroupName}
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}>
                  <div className={styles.peerGroupLine}>
                    <InfoOutlinedIcon
                      fontSize="small"
                      className={styles.peerGroupIcon}
                    />
                    <span className={styles.peerGroupLabel}>
                      {isMultiStoreEnabled
                        ? lang.storeBanner.yourMultiStoreGroup
                        : lang.storeBanner.yourStorePeerGroup}
                    </span>
                  </div>
                </Tooltip>
              </div>
              {isMobile && userChainList?.length > 1 && (
                <div className={styles.storeButtonWrapper}>
                  <MuiSelector
                    value={userDetails?.currentStoreNumber || ''}
                    onChange={handleStoreChange}
                    options={userChainList}
                  />
                </div>
              )}
            </div>
          </Grid>

          {/* Higher Ground Status Section */}
          <Grid
            size={4}
            className={styles.gridHigherGround}>
            <div className={styles.higherGroundSection}>
              <div className={styles.higherGroundWrapper}>
                <p className={styles.higherGroundLabel}>{lang.storeBanner.higherGroundStatus}:</p>

                <div className={styles.higherGroundChipContainer}>
                  <MuiChip
                    label={normalizedHigherGroundStatus}
                    size={isTablet ? 'small' : 'medium'}
                    colors="red"
                  />
                </div>

                {!isTablet && (
                  <div className={styles.higherGroundLogo}>
                    <img
                      src={higherGroundLogoImg}
                      alt="Higher Ground Logo"
                      className={styles.higherGroundLogoImg}
                    />
                  </div>
                )}

                <div className={styles.progressReportButton}>
                  <MuiButton
                    colors="white"
                    iconPosition="left"
                    iconType={isHGReportLoading ? 'restartAlt' : 'downloadOutlined'}
                    variant="outlined"
                    size={isTablet ? 'small' : 'medium'}
                    disabled={isHGReportLoading}
                    sx={{
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                    onClick={() => {
                      handleAndLogClick('Higher Ground Progress Report', {
                        callback: () => void handleProgressReportClick(),
                      });
                    }}>
                    {lang.storeBanner.progressReport}
                  </MuiButton>
                </div>
              </div>
            </div>
          </Grid>

          {/* Action Section */}
          <Grid
            size={4}
            className={styles.gridAction}>
            <div className={styles.actionSection}>
              <MuiButton
                colors="white"
                iconPosition="left"
                iconType="trendingUp"
                variant="outlined"
                size={isTablet ? 'small' : 'medium'}
                onClick={() => {
                  const url = buildDynamicUrl(baseUrlType, 'RGGP');
                  handleAndLogClick('My Business Plan', {
                    url,
                  });
                }}>
                {lang.storeBanner.myBusinessPlan}
              </MuiButton>

              {userChainList?.length > 1 && (
                <div className={styles.storeButtonWrapper}>
                  <MuiSelector
                    value={userDetails?.currentStoreNumber || ''}
                    onChange={handleStoreChange}
                    options={userChainList}
                    height={isTablet && !isMobile ? '31px' : undefined}
                  />
                </div>
              )}

              <MuiButton
                colors="white"
                iconPosition="center"
                iconType="questionMark"
                sx={{
                  borderRadius: '4px',
                  minWidth: '38px',
                  padding: '5px 0',
                  marginRight: '10px',
                  ...(isTablet && { height: '31px' }),
                }}
                variant="outlined"
                onClick={() => {
                  const url = buildDynamicUrl('ACENET', 'USER_GUIDE');
                  handleAndLogClick('User Guide', {
                    url,
                  });
                }}
              />
            </div>
          </Grid>
        </Grid>
      </Box>

      {/* Mobile Drawer */}
      <StoreBannerMobile
        currentStore={currentStore}
        userChainListLength={userDetails?.userChainList?.length || 0}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        higherGroundStatus={normalizedHigherGroundStatus}
        higherGroundLogoImg={higherGroundLogoImg}
        onPerformanceClick={onPerformanceClick}
        onOperationsClick={onOperationsClick}
        onADWReportClick={onADWReportClick}
        onADWDashboardClick={onADWDashboardClick}
        onMultiStoreToggle={onMultiStoreToggle}
        onPrintClick={onPrintClick}
        isPrintDisabled={isPrintDisabled}
        isMultiStoreEnabled={isMultiStoreEnabled}
        onProgressReportClick={() =>
          handleAndLogClick('Higher Ground Progress Report', {
            callback: () => void handleProgressReportClick(),
          })
        }
        onSummaryClick={onSummaryClick}
      />

      {/* Progress Report Dialog */}
      <MuiDialogue
        open={isProgressReportDialogOpen}
        hideTriggerButton
        dialogTitle={lang.storeBanner.progressReport}
        onClose={handleCloseProgressReportDialog}
        dialogContent={
          <Box>
            <Typography
              variant="body1"
              gutterBottom>
              {lang.pageContent.dataUnavailableError.replace(
                '{section}',
                'Higher Ground Progress Report',
              )}
            </Typography>
          </Box>
        }
      />
    </AppBar>
  );
};

export default StoreBanner;
